// atoms/Toggle.tsx
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

type Props = {
  id?: string;
  checked?: boolean;
  onChange?: (value: boolean) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  status?: 'error' | 'success';
  helperText?: string;
  'data-testid'?: string;
};

const Toggle = ({
  id,
  checked = false,
  onChange,
  className,
  size = 'md',
  disabled,
  'data-testid': dataTestId,
}: Props) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const isLocaleArabic = locale === 'ar';

  const sizes = {
    sm: {
      container: 'h-5 w-10',
      track: 'h-3 w-10',
      thumb: 'h-5 w-5',
      innerRing: 'h-2.5 w-2.5 border-[1.5px]',
      translateChecked: 'translate-x-5',
      translateCheckedRTL: '-translate-x-5',
    },
    md: {
      container: 'h-7 w-14',
      track: 'h-4 w-14',
      thumb: 'h-7 w-7',
      innerRing: 'h-3.5 w-3.5 border-[1.5px]',
      translateChecked: 'translate-x-7',
      translateCheckedRTL: '-translate-x-7',
    },
    lg: {
      container: 'h-9 w-16',
      track: 'h-5 w-16',
      thumb: 'h-9 w-9',
      innerRing: 'h-4 w-4 border-2',
      translateChecked: 'translate-x-7',
      translateCheckedRTL: '-translate-x-7',
    },
  };

  const s = sizes[size];

  return (
    <label
      htmlFor={id}
      className={classNames(
        'relative inline-flex cursor-pointer items-center',
        className,
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <input
        id={id}
        type="checkbox"
        data-testid={dataTestId}
        className="peer sr-only"
        checked={checked}
        onChange={(e) => !disabled && onChange?.(e.target.checked)}
        disabled={disabled}
      />
      <div className={classNames('relative flex items-center', s.container)}>
        {/* Track */}
        <div
          className={classNames(
            'absolute inset-0 m-auto rounded-full transition-colors duration-300',
            'bg-neutral-200 dark:bg-neutral-600',
            s.track
          )}
        />
        {/* Thumb */}
        <div
          className={classNames(
            'absolute left-0 rtl:left-auto rtl:right-0 rounded-full transition-all duration-300 flex items-center justify-center',
            s.thumb,
            checked
              ? 'bg-[#FD9FFD] shadow-sm'
              : 'bg-white border border-neutral-300 shadow-sm dark:bg-neutral-300 dark:border-neutral-400',
            checked && !isLocaleArabic && s.translateChecked,
            checked && isLocaleArabic && s.translateCheckedRTL,
            !checked && 'translate-x-0'
          )}
        >
          {/* Inner Ring */}
          <div
            className={classNames(
              'rounded-full border-white transition-opacity duration-300',
              s.innerRing,
              checked ? 'opacity-100' : 'opacity-0'
            )}
          />
        </div>
      </div>
    </label>
  );
};

export default Toggle;
